import "jspdf-autotable";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as Download } from "../../../assets/icons/download.svg";
import { ReactComponent as LeftArrow } from "../../../assets/icons/leftArrow.svg";
import { filterBy } from "../../../constants/constants";

import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { getSnapshotDetails } from "../../../app/api/trendSnapshot";
import { RootState } from "../../../app/store";
import { handleDownload } from "../../../utils/trendSidebarGraphUtils/overallAnalysisUtils";
import { getOverallAnalysisDynamicColumnKeys } from "../../../utils/trendSidebarGraphUtils/overallAnalysisColumns";
import Dropdown from "../../Common/Dropdown";
import OverallAnalysisGraph from "../TrendsSidebar/OverallAnalysisGraph";
import OverallAnalysisTable from "../TrendsSidebar/OverallAnalysisTable";
import { BarData } from "../../../interface/Trends";
import { filterArchivedGraphRows } from "../../../utils/archivedTrends";
import { checkSnapshotStatus } from "../../../utils/snapshotHelpers";

interface ExpandViewOverallAnalysisProps {
  isSidebarOpen?: boolean;
}

const ExpandedViewSnapShotOverallAnalysis: React.FC<
  ExpandViewOverallAnalysisProps
> = ({ isSidebarOpen }) => {
  const [dynamicColumns, setDynamicColumns] = useState<string[]>([]);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [trendFilterType, setTrendFilterType] =
    useState<string>("Most Popular");
  const navigate = useNavigate();

  const [barData, setBarData] = React.useState<BarData[] | null>(null);
  const [sortingType, setSortingType] = useState("Trend Score");
  const graphContainerRef = useRef<HTMLDivElement | null>(null);
  const [snapshotStatus, setSnapshotStatus] = useState<string>("");
  const user = useSelector((state: RootState) => state.user.userInfo);
  const { id } = useParams();
  const numericSnapshotId = Number(id);
  const fetchDetails = async (page: number) => {
    setLoading(true);
    const detailsParams = {
      page: page,
      numberOfTrends: 1,
      postsPerTrend: 1,
    };
    try {
      const snapDetails = await getSnapshotDetails(
        numericSnapshotId,
        user,
        detailsParams
      );
      const { status, isNotCompleted } = checkSnapshotStatus(snapDetails);
      setSnapshotStatus(status);
      if (isNotCompleted) {
        return;
      }
      const archivedTrendNames =
        snapDetails.data.snapshot?.genericFilters?.archivedTrendNames || [];
      const barDataResponse = filterArchivedGraphRows(
        (snapDetails.data.barGraphData ?? []) as BarData[],
        archivedTrendNames
      );

      if (sortingType === "Trend Score") {
        setBarData(barDataResponse);
      } else {
        const sortedByImpact = [...barDataResponse].sort(
          (a, b) =>
            (Number(b["Impact Score"]) || 0) - (Number(a["Impact Score"]) || 0)
        );
        setBarData(sortedByImpact);
      }
      setTrendFilterType(
        snapDetails?.data?.snapshot?.genericFilters?.graphsFilters
          ?.query_type || "Most Popular"
      );
      //Fetch the table contents
      if (barDataResponse?.length > 0) {
        setDynamicColumns(
          getOverallAnalysisDynamicColumnKeys(
            barDataResponse[0] as Record<string, unknown>
          )
        );
      }
    } catch (error) {
      console.error("Error fetching snapshot details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails(1);
  }, [sortingType]);

  useEffect(() => {
    if (snapshotStatus === "pending" || snapshotStatus === "processing") {
      const interval = setInterval(() => {
        fetchDetails(1);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [snapshotStatus, sortingType]);

  if (snapshotStatus === "completed" && barData && barData.length === 0) {
    return <div className="my-10 text-center">No Bar Data</div>;
  }

  return (
    <div
      className={`bg-white fixed h-[calc(100vh-80px)] ml-2 ${!isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"} transition-all duration-500 ease-in-out`}
    >
      <div className="flex flex-row justify-between mb-4">
        <div className="flex flex-row mt-6">
          <div>
            <LeftArrow
              className="text-lg ml-4 mt-4 cursor-pointer"
              onClick={() => navigate(`/trend-snapshot/details/${id}`)}
            />
          </div>
          <div className="transition-all  duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-4 mt-2.5 text-secondary ">
            Overall Analysis
          </div>
        </div>
        <div className="mt-2 mr-4 flex items-end">
          <button
            onClick={() =>
              handleDownload(
                setDownloadLoading,
                barData,
                dynamicColumns,
                undefined,
                graphContainerRef.current ?? undefined
              )
            }
            disabled={downloadLoading}
            title="Download Overall Analysis Report"
            className="flex items-center gap-2 mr-4 border border-gray-200 rounded-md px-3 py-1.5 bg-white text-secondary hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {downloadLoading ? (
              <span className="inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
            ) : (
              <Download width={20} height={20} />
            )}
          </button>
          <div>
            <div className="text-sm">Filter By</div>
            <div className="flex  gap-4 mt-2">
              <div className="w-48">
                <Dropdown
                  label={sortingType || "Select type"}
                  options={filterBy}
                  onSelect={option => {
                    if (option !== sortingType) setSortingType(option);
                  }}
                />
              </div>
              <div className="w-48">
                <Dropdown
                  label={trendFilterType}
                  options={[]}
                  onSelect={option => {}}
                  trendSnapshotView={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className=" overflow-auto h-[calc(100vh-150px)] pb-4">
        {!barData || loading ? (
          <div className="my-10 flex items-center justify-center">
            <Skeleton height={350} width={1200} />
          </div>
        ) : (
          <div
            ref={graphContainerRef}
            className="my-10 flex items-center justify-center "
          >
            <OverallAnalysisGraph barData={barData} expanded={true} />
          </div>
        )}

        {!barData || loading ? (
          <div className="my-10 flex items-center justify-center">
            {/* Generate the skeleton rows */}
            {Array.from({ length: 8 }).map((_, index) => (
              <div>
                {Array.from({ length: 7 }).map((_, colIndex) => (
                  <Skeleton
                    key={`${index}-${colIndex}`}
                    width={100}
                    className="mx-4"
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <OverallAnalysisTable
            dynamicColumns={dynamicColumns}
            barData={barData}
            selectedMaterials={
              barData[0] != null && "Color" in barData[0] ? ["Color"] : []
            }
          />
        )}
      </div>
    </div>
  );
};

export default ExpandedViewSnapShotOverallAnalysis;
